import React, { useState, useEffect } from "react";
import { graphql, useStaticQuery } from "gatsby";
import { iconMap } from "../card/card";
import { TabbedResults } from "../tabbed-results";
import { PromptDisplay } from "../prompt-display";
import { capitalizeFirstLetter, cn, toPlainText } from "../../lib/helpers";

import * as styles from "../global/global.module.css";
import { pageDocPath } from "../../../helpers";

const MENU = [
  {
    label: "All",
    slug: "",
  },
  {
    label: "Sales-led",
    slug: "sales",
  },
  {
    label: "Product-led",
    slug: "product-led-growth",
  },
  {
    label: "Open-source",
    slug: "commercial-open-source-software",
  },
  {
    label: "Community-led",
    slug: "community-teams",
  },
  {
    label: "Dark-funnel",
    slug: "dark-funnel",
  },
];

const STRENGTH_OPTIONS = [
  {
    label: "All",
    value: "",
  },
  {
    label: "High",
    value: "High",
  },
  {
    label: "Medium",
    value: "Medium",
  },
  {
    label: "Low",
    value: "Low",
  },
];

const query = graphql`
  query PromptSelectorQuery {
    scenarios: allSanityScenario(
      sort: { fields: [title], order: ASC }
      filter: { slug: { current: { ne: null } } }
    ) {
      edges {
        node {
          _id
          _type
          title
          slug {
            current
          }
          _rawExcerpt
          researchPrompt
          outgoingPrompt
          researchPromptResults
          outgoingPromptResults
        }
      }
    }
    signals: allSanitySignal(
      sort: { fields: [title], order: ASC }
      filter: { slug: { current: { ne: null } } }
    ) {
      edges {
        node {
          _id
          _type
          title
          _rawExcerpt
          signalSource {
            _id
            title
            slug {
              current
            }
          }
          strength
          sourceType
          categories {
            _id
            title
            slug {
              current
            }
          }
        }
      }
    }
  }
`;

export const PromptSelector = ({ title, description, bgColor, className }) => {
  const data = useStaticQuery(query);
  const scenarios = data.scenarios.edges.map(edge => edge.node);
  const signals = data.signals.edges.map(edge => edge.node);

  const [selectedItem, setSelectedItem] = useState(null);
  const [isOutbound, setIsOutbound] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState("");
  const [selectedStrength, setSelectedStrength] = useState("");
  const [selectedSignalSource, setSelectedSignalSource] = useState("");
  const [selectedSourceType, setSelectedSourceType] = useState("");
  const [searchQuery, setSearchQuery] = useState("");

  // Handle URL parameter on page load - needs to be before any early returns
  useEffect(() => {
    if (typeof window !== 'undefined' && data?.scenarios && data?.signals) {
      const urlParams = new URLSearchParams(window.location.search);
      const idParam = urlParams.get('id');

      if (idParam) {
        // Create scenarios map
        const scenariosByTitle = new Map();
        const scenarios = data.scenarios.edges.map(edge => edge.node);
        scenarios.forEach(scenario => {
          scenariosByTitle.set(scenario.title, scenario);
        });

        // Create signal items with scenario data
        const signals = data.signals.edges.map(edge => edge.node);
        const signalItems = signals.map(signal => {
          const matchingScenario = scenariosByTitle.get(signal.title);
          return {
            ...signal,
            itemType: 'signal',
            hasPrompts: Boolean(matchingScenario),
            researchPrompt: matchingScenario?.researchPrompt || null,
            outgoingPrompt: matchingScenario?.outgoingPrompt || null,
            researchPromptResults: matchingScenario?.researchPromptResults || null,
            outgoingPromptResults: matchingScenario?.outgoingPromptResults || null,
            slug: matchingScenario?.slug || signal.slug,
            scenarioSlug: matchingScenario?.slug?.current
          };
        });

        // Find the signal item that matches the scenario slug
        const matchingItem = signalItems.find(item => item.scenarioSlug === idParam);

        if (matchingItem && matchingItem.hasPrompts) {
          setSelectedItem(matchingItem);
        }
      }
    }
  }, [data]);

  // Ensure data exists and has the expected structure
  if (!data || !data.scenarios || !data.signals) {
    console.error('PromptSelector: Invalid data structure from GraphQL query');
    return (
      <section className={cn(styles.section, styles[bgColor], className)}>
        <div className={styles.container}>
          <p>Error loading prompt selector data.</p>
        </div>
      </section>
    );
  }

  // Function to check if an item matches the selected category, strength, signal source, source type, and search query
  const matchesFilters = (item, categorySlug, strengthValue, signalSourceSlug, sourceTypeValue, searchTerm = "") => {
    // Search filter
    if (searchTerm && searchTerm.trim()) {
      const searchLower = searchTerm.toLowerCase().trim();
      const titleMatch = item.title?.toLowerCase().includes(searchLower);
      const excerptMatch = item._rawExcerpt ?
        toPlainText(item._rawExcerpt).toLowerCase().includes(searchLower) :
        false;

      if (!titleMatch && !excerptMatch) return false;
    }

    // Category filter
    if (categorySlug && item.itemType === 'signal' && item.categories) {
      const matchesCategory = item.categories.some(cat => cat.slug?.current === categorySlug);
      if (!matchesCategory) return false;
    } else if (categorySlug && item.itemType === 'scenario') {
      // For scenarios, we don't have category filtering implemented yet
      // so we'll show all scenarios regardless of category filter
    }

    // Strength filter - only applies to signals
    if (strengthValue && item.itemType === 'signal') {
      if (item.strength !== strengthValue) return false;
    } else if (strengthValue && item.itemType === 'scenario') {
      // Scenarios don't have strength, so hide them when strength filter is active
      return false;
    }

    // Signal source filter - only applies to signals
    if (signalSourceSlug && item.itemType === 'signal') {
      if (!item.signalSource || !item.signalSource.some(source => source.slug?.current === signalSourceSlug)) {
        return false;
      }
    } else if (signalSourceSlug && item.itemType === 'scenario') {
      // Scenarios don't have signalSource, so hide them when signal source filter is active
      return false;
    }

    // Source type filter - only applies to signals
    if (sourceTypeValue && item.itemType === 'signal') {
      if (!item.sourceType || !item.sourceType.includes(sourceTypeValue)) {
        return false;
      }
    } else if (sourceTypeValue && item.itemType === 'scenario') {
      // Scenarios don't have sourceType, so hide them when source type filter is active
      return false;
    }

    return true;
  };

  // Extract unique signal sources from signals data
  const signalSourcesSet = new Set();
  signals.forEach(signal => {
    if (signal.signalSource && Array.isArray(signal.signalSource)) {
      signal.signalSource.forEach(source => {
        if (source.slug?.current && source.title) {
          signalSourcesSet.add(JSON.stringify({
            slug: source.slug.current,
            title: source.title
          }));
        }
      });
    }
  });

  // Extract unique source types from signals data
  const sourceTypesSet = new Set();
  signals.forEach(signal => {
    if (signal.sourceType && Array.isArray(signal.sourceType)) {
      signal.sourceType.forEach(sourceType => {
        if (sourceType && typeof sourceType === 'string') {
          sourceTypesSet.add(sourceType);
        }
      });
    }
  });

  // Create a map of scenarios by title for quick lookup
  const scenariosByTitle = new Map();
  scenarios.forEach(scenario => {
    scenariosByTitle.set(scenario.title, scenario);
  });

  // Create signal items, enriching them with prompt data from matching scenarios
  const signalItems = signals.map(signal => {
    const matchingScenario = scenariosByTitle.get(signal.title);
    return {
      ...signal,
      itemType: 'signal',
      hasPrompts: Boolean(matchingScenario),
      // Add prompt data from matching scenario if it exists
      researchPrompt: matchingScenario?.researchPrompt || null,
      outgoingPrompt: matchingScenario?.outgoingPrompt || null,
      researchPromptResults: matchingScenario?.researchPromptResults || null,
      outgoingPromptResults: matchingScenario?.outgoingPromptResults || null,
      // Ensure we have the scenario slug for linking
      slug: matchingScenario?.slug || signal.slug,
      // Store the scenario slug for URL parameter matching
      scenarioSlug: matchingScenario?.slug?.current
    };
  });

  const combinedItems = signalItems
    .filter(item => matchesFilters(item, selectedCategory, selectedStrength, selectedSignalSource, selectedSourceType, searchQuery))
    .sort((a, b) => a.title.localeCompare(b.title));

  // Get all items for count calculations (before filtering)
  const allItems = signalItems;

  // Calculate counts for enabled vs disabled items for better labeling
  const enabledItemsCount = combinedItems.filter(item => item.hasPrompts).length;
  const disabledItemsCount = combinedItems.filter(item => !item.hasPrompts).length;

  // Helper function to calculate counts for filter options
  const calculateFilterCounts = () => {
    // Category counts
    const categoryCounts = {};
    MENU.forEach(category => {
      categoryCounts[category.slug] = allItems.filter(item =>
        matchesFilters(item, category.slug, "", "", "", searchQuery)
      ).length;
    });

    // Strength counts
    const strengthCounts = {};
    STRENGTH_OPTIONS.forEach(option => {
      strengthCounts[option.value] = allItems.filter(item =>
        matchesFilters(item, "", option.value, "", "", searchQuery)
      ).length;
    });

    // Signal source counts
    const signalSourceCounts = {};
    signalSourceCounts[""] = allItems.filter(item =>
      matchesFilters(item, "", "", "", "", searchQuery)
    ).length; // All sources with search
    Array.from(signalSourcesSet).forEach(sourceStr => {
      const source = JSON.parse(sourceStr);
      signalSourceCounts[source.slug] = allItems.filter(item =>
        matchesFilters(item, "", "", source.slug, "", searchQuery)
      ).length;
    });

    // Source type counts
    const sourceTypeCounts = {};
    sourceTypeCounts[""] = allItems.filter(item =>
      matchesFilters(item, "", "", "", "", searchQuery)
    ).length; // All types with search
    Array.from(sourceTypesSet).forEach(sourceType => {
      sourceTypeCounts[sourceType] = allItems.filter(item =>
        matchesFilters(item, "", "", "", sourceType, searchQuery)
      ).length;
    });

    return { categoryCounts, strengthCounts, signalSourceCounts, sourceTypeCounts };
  };

  const { categoryCounts, strengthCounts, signalSourceCounts, sourceTypeCounts } = calculateFilterCounts();

  // Create options arrays with counts
  const categoryOptionsWithCounts = MENU.map(category => ({
    ...category,
    label: `${category.label} (${categoryCounts[category.slug] || 0})`
  }));

  const strengthOptionsWithCounts = STRENGTH_OPTIONS.map(option => ({
    ...option,
    label: `${option.label} (${strengthCounts[option.value] || 0})`
  }));

  // Create options arrays with counts
  const signalSourceOptions = [
    { label: `All sources (${signalSourceCounts[""] || 0})`, value: "" },
    ...Array.from(signalSourcesSet)
      .map(sourceStr => JSON.parse(sourceStr))
      .sort((a, b) => a.title.localeCompare(b.title))
      .map(source => ({
        label: `${source.title} (${signalSourceCounts[source.slug] || 0})`,
        value: source.slug
      }))
  ];

  const sourceTypeOptions = [
    { label: `All types (${sourceTypeCounts[""] || 0})`, value: "" },
    ...Array.from(sourceTypesSet)
      .sort()
      .map(sourceType => ({
        label: `${iconMap.get(sourceType)?.label || capitalizeFirstLetter(sourceType)} (${sourceTypeCounts[sourceType] || 0})`,
        value: sourceType
      }))
  ];

  // Calculate the current prompt content based on selected item and outbound flag
  const getPromptContent = () => {
    if (!selectedItem || !selectedItem.hasPrompts) {
      return "";
    }

    const prompt = isOutbound
      ? selectedItem.outgoingPrompt
      : selectedItem.researchPrompt;

    return (prompt && typeof prompt === 'string') ? prompt : "No prompt available for this selection.";
  };

  const handleItemChange = (event) => {
    const itemId = event.target.value;
    if (!itemId) {
      setSelectedItem(null);
      // Remove the id parameter from URL when no item is selected
      if (typeof window !== 'undefined') {
        const url = new URL(window.location);
        url.searchParams.delete('id');
        window.history.replaceState({}, '', url.toString());
      }
      return;
    }
    const item = combinedItems.find(i => i._id === itemId) || null;
    setSelectedItem(item);

    // Update URL parameter with the scenario slug
    if (item && item.scenarioSlug && typeof window !== 'undefined') {
      const url = new URL(window.location);
      url.searchParams.set('id', item.scenarioSlug);
      window.history.replaceState({}, '', url.toString());
    }
  };

  const handleCategoryChange = (event) => {
    setSelectedCategory(event.target.value);
    // Reset selected item when category changes since the list will change
    setSelectedItem(null);
  };

  const handleStrengthChange = (event) => {
    setSelectedStrength(event.target.value);
    // Reset selected item when strength changes since the list will change
    setSelectedItem(null);
  };

  const handleSignalSourceChange = (event) => {
    setSelectedSignalSource(event.target.value);
    // Reset selected item when signal source changes since the list will change
    setSelectedItem(null);
  };

  const handleSourceTypeChange = (event) => {
    setSelectedSourceType(event.target.value);
    // Reset selected item when source type changes since the list will change
    setSelectedItem(null);
  };

  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
    // Reset selected item when search changes since the list will change
    setSelectedItem(null);
  };

  // Calculate the current prompt content once
  const promptContent = getPromptContent();

  return (
    <section
      className={cn(
        styles.section,
        styles[bgColor],
        className
      )}
    >
      <div className={styles.container}>
        {title && (
          <div className={styles.header}>
            <h2 className={styles.title}>{title}</h2>
            {description && (
              <p className={styles.description}>{description}</p>
            )}
          </div>
        )}

        <div className="max-w-4xl mx-auto space-y-3">
          <div className="border-2 border-green-emerald p-3 rounded-xl space-y-6 bg-light-30">
            {/* Category, Strength, Signal Source, Source Type Filters Row */}
            <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 sm:gap-6">
              {/* Category Filter */}
              {/* <div>
                <label
                  htmlFor="category-select"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  Category
                </label>
                <select
                  id="category-select"
                  value={selectedCategory}
                  onChange={handleCategoryChange}
                  className="w-full px-3 py-2 bg-white border border-dark-10 rounded-md shadow-sm focus:outline-none focus:ring-0 focus:ring-0"
                >
                  {categoryOptionsWithCounts.map((category) => (
                    <option key={category.slug} value={category.slug}>
                      {category.label}
                    </option>
                  ))}
                </select>
              </div> */}

              {/* Source Type Filter */}
              <div>
                <label
                  htmlFor="source-type-select"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  Signal
                </label>
                <select
                  id="source-type-select"
                  value={selectedSourceType}
                  onChange={handleSourceTypeChange}
                  className="w-full px-3 py-2 bg-white border border-dark-10 rounded-md shadow-sm focus:outline-none focus:ring-0 focus:ring-0"
                >
                  {sourceTypeOptions.map((sourceType) => (
                    <option key={sourceType.value} value={sourceType.value}>
                      {sourceType.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Signal Source Filter */}
              {/* <div>
                <label
                  htmlFor="signal-source-select"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  Signal Group
                </label>
                <select
                  id="signal-source-select"
                  value={selectedSignalSource}
                  onChange={handleSignalSourceChange}
                  className="w-full px-3 py-2 bg-white border border-dark-10 rounded-md shadow-sm focus:outline-none focus:ring-0 focus:ring-0"
                >
                  {signalSourceOptions.map((source) => (
                    <option key={source.value} value={source.value}>
                      {source.label}
                    </option>
                  ))}
                </select>
              </div> */}

              {/* Search Input Row */}
              <div className="col-span-2">
                <label
                  htmlFor="search-input"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  Search
                </label>
                <input
                  id="search-input"
                  type="text"
                  value={searchQuery}
                  onChange={handleSearchChange}
                  placeholder="Search signal keywords..."
                  className="w-full px-3 py-2 bg-white border border-dark-10 rounded-md shadow-sm focus:outline-none focus:ring-0 focus:ring-0"
                />
              </div>

              {/* Strength Filter */}
              <div>
                <label
                  htmlFor="strength-select"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  Strength
                </label>
                <select
                  id="strength-select"
                  value={selectedStrength}
                  onChange={handleStrengthChange}
                  className="w-full px-3 py-2 bg-white border border-dark-10 rounded-md shadow-sm focus:outline-none focus:ring-0 focus:ring-0"
                >
                  {strengthOptionsWithCounts.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Signal Scenario Selection Row */}
            <div>
              {/* Signal Dropdown */}
              <div>
                <label
                  htmlFor="scenario-select"
                  className="block text-sm font-semibold text-black mb-2"
                >
                  Signal <span className="text-auxiliary">({enabledItemsCount} with prompts{disabledItemsCount > 0 ? `, ${disabledItemsCount} guides only` : ''})</span>
                </label>
                <select
                  id="scenario-select"
                  value={selectedItem?._id || ""}
                  onChange={handleItemChange}
                  size={7}
                  className="w-full px-3 py-2 bg-white border border-dark-10 rounded-md shadow-sm focus:outline-none focus:ring-0 focus:ring-0 focus:border-0"
                >
                  {combinedItems.length === 0 ? (
                    <option value="" disabled>No signals match your filters</option>
                  ) : (
                    <>
                      <option value="" className="text-gray-500 italic">- Select signal scenario -</option>
                      {combinedItems.map((item) => (
                        <option
                          key={item._id}
                          value={item._id}
                          disabled={!item.hasPrompts}
                          className={cn("py-1", !item.hasPrompts ? 'text-gray-400' : '')}
                        >
                          {item.hasPrompts ?
                            `📊 ${item.title}` :
                            `📊 ${item.title} (guide only)`
                          }
                        </option>
                      ))}
                    </>
                  )}
                </select>
                {selectedItem && !selectedItem.hasPrompts && (
                  <p className="text-sm text-amber-600 mt-1">
                    ⚠️ This signal guide only - no prompts available yet.
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Centered Action Toggle */}
          <div className="flex flex-col items-center">
            <div className={`flex items-center gap-2 ${!selectedItem || !selectedItem.hasPrompts ? 'opacity-50' : ''}`}>
              <button
                type="button"
                onClick={() => setIsOutbound(false)}
                disabled={!selectedItem || !selectedItem.hasPrompts}
                className={`px-8 py-2 text-sm font-medium rounded-md transition-all duration-200 ${!isOutbound
                  ? 'bg-green-emerald text-white shadow-sm'
                  : 'bg-dark-10 text-black hover:bg-dark-20'
                  } ${!selectedItem || !selectedItem.hasPrompts ? 'cursor-not-allowed' : 'cursor-pointer'}`}
              >
                Research
              </button>
              <button
                type="button"
                onClick={() => setIsOutbound(true)}
                disabled={!selectedItem || !selectedItem.hasPrompts}
                className={`px-8 py-2 text-sm font-medium rounded-md transition-all duration-200 ${isOutbound
                  ? 'bg-green-emerald text-white shadow-sm'
                  : 'bg-dark-10 text-black hover:bg-dark-20'
                  } ${!selectedItem || !selectedItem.hasPrompts ? 'cursor-not-allowed' : 'cursor-pointer'}`}
              >
                Outbound
              </button>
            </div>
            {selectedItem && !selectedItem.hasPrompts && (
              <p className="text-xs text-black mt-1 text-center">
                Prompts coming soon for this signal
              </p>
            )}
          </div>

          {/* Console-styled Prompt Display */}
          <div>
            <PromptDisplay
              content={promptContent}
              placeholder="Select signal to see prompt..."
            />
          </div>

          {/* Tabbed Results - Show example results for the selected prompt */}
          <div>
            {selectedItem && selectedItem.hasPrompts ? (
              <TabbedResults
                results={isOutbound ? selectedItem.outgoingPromptResults : selectedItem.researchPromptResults}
              />
            ) : (
              <div className="bg-light-5 border border-black rounded-xl p-8 text-center">
                <p className="text-lg text-gray-600">
                  Pick a signal to see example results.
                </p>
              </div>
            )}
          </div>

          {selectedItem && selectedItem.hasPrompts && (
            <div id="signal-guide-link" className="mt-6">
              <a
                href={`/scenarios/${selectedItem.slug?.current}/${isOutbound ? 'outbound' : 'research'}-prompt/`}
                className="flex justify-between items-center font-medium text-white bg-green-emerald rounded-lg p-3 mt-4 transform scale-100 hover:scale-102 transition-transform"
                target="_blank"
              >
                <p className="font-normal">
                  {toPlainText(selectedItem._rawExcerpt)}
                </p>
                <p>Bookmark this prompt</p>
              </a>
            </div>
          )}

        </div>
      </div>
    </section>
  );
};
